import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  Container,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Collapse,
  Badge,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  ArrowUpward,
  ArrowDownward,
  Warning as WarningIcon,
  Notifications as NotificationsIcon,
  LocationOn as LocationIcon,
  Update as UpdateIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assignment as TaskIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useNotification } from '../contexts/NotificationContext';
import { format, isToday } from 'date-fns';

interface Borrower {
  id: string;
  name: string;
  mobile_no: string;
  address: string;
}

interface BorrowerWithUpdate extends Borrower {
  last_update?: string;
  update_count?: number;
  distance?: number;
}

interface BorrowerReminder {
  borrower: Borrower;
  reason: string;
  priority: 'high' | 'medium' | 'low';
  days_since_update: number;
}

interface ItineraryItem {
  id?: string;
  item_type: 'borrower' | 'task';
  borrower_id?: string;
  borrower?: Borrower;
  task_title?: string;
  task_description?: string;
  visit_order: number;
  comments: string;
}

type ItineraryBorrower = ItineraryItem;

interface Itinerary {
  id?: string;
  user_id: string;
  planned_date: Date;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'revision_requested';
  manager_comment?: string;
  borrowers: ItineraryBorrower[];
}

export default function ItineraryPlanner() {
  const { userProfile } = useAuth();
  const { showSuccess, showError } = useNotification();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plannedDate, setPlannedDate] = useState<Date | null>(new Date());
  const [selectedBorrowers, setSelectedBorrowers] = useState<ItineraryItem[]>([]);
  // Track the last-saved state so we can detect unsaved changes
  const [savedBorrowers, setSavedBorrowers] = useState<ItineraryItem[]>([]);
  const [currentItinerary, setCurrentItinerary] = useState<Itinerary | null>(null);
  const [borrowerSearchOpen, setBorrowerSearchOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Borrower[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [reminders, setReminders] = useState<BorrowerReminder[]>([]);
  const [remindersOpen, setRemindersOpen] = useState(!isMobile);
  const [suggestedBorrowers, setSuggestedBorrowers] = useState<BorrowerWithUpdate[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [recentUpdates, setRecentUpdates] = useState<Map<string, number>>(new Map());

  const canApproveAndEditApproved =
    userProfile?.role === 'admin' ||
    userProfile?.role === 'manager' ||
    userProfile?.role === 'superagent';
  const canEdit = userProfile?.role === 'agent' || canApproveAndEditApproved;

  // Detect unsaved changes by comparing serialized content (ignores DB id field)
  const normalizeBorrowers = (items: ItineraryItem[]) =>
    JSON.stringify(
      items.map(b => ({
        item_type: b.item_type,
        borrower_id: b.borrower_id ?? null,
        task_title: b.task_title ?? null,
        task_description: b.task_description ?? null,
        visit_order: b.visit_order,
        comments: b.comments,
      }))
    );
  const hasChanges = normalizeBorrowers(selectedBorrowers) !== normalizeBorrowers(savedBorrowers);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsedDate = new Date(dateParam);
      if (!isNaN(parsedDate.getTime())) {
        setPlannedDate(parsedDate);
      }
    }
  }, [location.search]);

  // NOTE: depend on userProfile?.id (a stable primitive) — NOT the whole
  // userProfile object. Supabase fires onAuthStateChange on token refresh
  // (~hourly) and tab focus, which recreates the userProfile object reference.
  // Keying on the object caused loadItinerary() to re-run mid-edit and overwrite
  // unsaved borrowers/tasks with the last-saved DB state (entries lost on refresh).
  useEffect(() => {
    if (plannedDate && userProfile) {
      // Load the itinerary first so the page is interactive immediately,
      // then defer the heavier background queries by one tick.
      loadItinerary();
      const timer = setTimeout(() => {
        loadReminders();
        loadRecentUpdates();
      }, 0);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedDate, userProfile?.id]);

  useEffect(() => {
    if (selectedBorrowers.length > 0) {
      loadNearbySuggestions();
    } else {
      setSuggestedBorrowers([]);
      setSuggestionsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBorrowers]);

  const loadItinerary = async () => {
    if (!plannedDate || !userProfile) return;
    setLoading(true);
    try {
      const dateStr = format(plannedDate, 'yyyy-MM-dd');
      const { data, error } = await supabase
        .from('itineraries')
        .select(`
          *,
          itinerary_borrowers (
            id,
            item_type,
            borrower_id,
            task_title,
            task_description,
            visit_order,
            comments,
            borrowers (
              id,
              name,
              mobile_no,
              address
            )
          )
        `)
        .eq('user_id', userProfile.id)
        .eq('planned_date', dateStr)
        .is('deleted_at', null)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        const borrowers: ItineraryItem[] = (data.itinerary_borrowers || [])
          .map((ib: any) => ({
            id: ib.id,
            item_type: ib.item_type || 'borrower',
            borrower_id: ib.borrower_id,
            borrower: ib.borrower_id
              ? (Array.isArray(ib.borrowers) ? ib.borrowers[0] : ib.borrowers)
              : undefined,
            task_title: ib.task_title,
            task_description: ib.task_description,
            visit_order: ib.visit_order,
            comments: ib.comments || '',
          }))
          .sort((a: ItineraryItem, b: ItineraryItem) => a.visit_order - b.visit_order);

        setCurrentItinerary({
          id: data.id,
          user_id: data.user_id,
          planned_date: new Date(data.planned_date),
          status: data.status,
          manager_comment: data.manager_comment,
          borrowers,
        });
        setSelectedBorrowers(borrowers);
        setSavedBorrowers(borrowers);
      } else {
        setCurrentItinerary(null);
        setSelectedBorrowers([]);
        setSavedBorrowers([]);
      }
    } catch (error: any) {
      console.error('Error loading itinerary:', error);
      showError('Failed to load itinerary');
    } finally {
      setLoading(false);
    }
  };

  const extractDatesFromText = (text: string): Date[] => {
    const dates: Date[] = [];
    const now = new Date();
    const patterns = [
      /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\b/g,
      /\b(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})\b/g,
      /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})\b/gi,
      /\b(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})\b/gi,
    ];
    patterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        try {
          const date = new Date(match[0]);
          if (date && !isNaN(date.getTime()) && date >= now) {
            dates.push(date);
          }
        } catch (e) {
          // skip invalid dates
        }
      }
    });
    return dates;
  };

  const loadReminders = async () => {
    if (!userProfile) return;
    try {
      const now = new Date();
      const reminderList: BorrowerReminder[] = [];

      const { data: updatesWithDates, error: updatesError } = await supabase
        .from('borrower_updates')
        .select(`
          id,
          content,
          created_at,
          collection_id,
          for_collection!inner (
            id,
            borrower_id,
            borrowers!inner (
              id,
              name,
              mobile_no,
              address
            )
          )
        `)
        .eq('for_collection.agent_id', userProfile.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!updatesError && updatesWithDates && updatesWithDates.length > 0) {
        updatesWithDates.forEach((update: any) => {
          const collection = Array.isArray(update.for_collection)
            ? update.for_collection[0]
            : update.for_collection;
          const borrower = collection?.borrowers
            ? (Array.isArray(collection.borrowers) ? collection.borrowers[0] : collection.borrowers)
            : null;

          if (borrower && update.content) {
            const upcomingDates = extractDatesFromText(update.content);
            if (upcomingDates.length > 0) {
              const nearestDate = upcomingDates.sort((a, b) => a.getTime() - b.getTime())[0];
              const daysUntil = Math.floor((nearestDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              if (daysUntil >= 0 && daysUntil <= 14) {
                const dateStr = nearestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const snippet = update.content.substring(0, 50) + (update.content.length > 50 ? '...' : '');
                reminderList.push({
                  borrower,
                  reason: `Upcoming: ${dateStr} – "${snippet}"`,
                  priority: daysUntil <= 3 ? 'high' : daysUntil <= 7 ? 'medium' : 'low',
                  days_since_update: -daysUntil,
                });
              }
            }
          }
        });
      }

      const { data, error } = await supabase
        .from('for_collection')
        .select(`
          id,
          borrower_id,
          borrowers!inner (
            id,
            name,
            mobile_no,
            address
          )
        `)
        .eq('agent_id', userProfile.id)
        .is('deleted_at', null)
        .limit(20);

      if (error) throw error;

      if (data && data.length > 0) {
        const { data: updates, error: updateError } = await supabase
          .from('borrower_updates')
          .select('collection_id, created_at')
          .in('collection_id', data.map((d: any) => d.id))
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        if (updateError) throw updateError;

        const updateMap = new Map();
        (updates || []).forEach((u: any) => {
          if (!updateMap.has(u.collection_id)) {
            updateMap.set(u.collection_id, new Date(u.created_at));
          }
        });

        data.forEach((item: any) => {
          const borrower = Array.isArray(item.borrowers) ? item.borrowers[0] : item.borrowers;
          if (!borrower) return;
          if (reminderList.some(r => r.borrower.id === borrower.id)) return;

          const lastUpdate = updateMap.get(item.id);
          const daysSince = lastUpdate
            ? Math.floor((Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24))
            : 999;

          if (daysSince >= 7) {
            reminderList.push({
              borrower,
              reason: lastUpdate ? `No updates for ${daysSince} days` : 'No updates recorded',
              priority: daysSince >= 30 ? 'high' : daysSince >= 14 ? 'medium' : 'low',
              days_since_update: daysSince,
            });
          }
        });
      }

      reminderList.sort((a, b) => {
        const w = { high: 3, medium: 2, low: 1 };
        if (w[a.priority] !== w[b.priority]) return w[b.priority] - w[a.priority];
        return b.days_since_update - a.days_since_update;
      });

      setReminders(reminderList.slice(0, 8));
    } catch (error: any) {
      console.error('Error loading reminders:', error);
    }
  };

  const loadRecentUpdates = async () => {
    if (!userProfile) return;
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data, error } = await supabase
        .from('borrower_updates')
        .select(`
          collection_id,
          created_at,
          for_collection!inner (
            borrower_id,
            agent_id
          )
        `)
        .eq('for_collection.agent_id', userProfile.id)
        .gte('created_at', sevenDaysAgo.toISOString())
        .is('deleted_at', null);

      if (error) throw error;

      const updateCounts = new Map<string, number>();
      (data || []).forEach((item: any) => {
        const collection = Array.isArray(item.for_collection) ? item.for_collection[0] : item.for_collection;
        if (collection?.borrower_id) {
          updateCounts.set(
            collection.borrower_id,
            (updateCounts.get(collection.borrower_id) || 0) + 1
          );
        }
      });
      setRecentUpdates(updateCounts);
    } catch (error: any) {
      console.error('Error loading recent updates:', error);
    }
  };

  const loadNearbySuggestions = async () => {
    if (selectedBorrowers.length === 0 || !userProfile) return;
    try {
      const borrowersOnly = selectedBorrowers.filter(
        b => b.item_type === 'borrower' && b.borrower && b.borrower_id
      );
      if (borrowersOnly.length === 0) return;

      const selectedAddresses = borrowersOnly.map(b => b.borrower!.address);
      const selectedIds = borrowersOnly.map(b => b.borrower_id!);

      const { data, error } = await supabase
        .from('for_collection')
        .select(`
          id,
          borrower_id,
          borrowers!inner (
            id,
            name,
            mobile_no,
            address
          )
        `)
        .eq('agent_id', userProfile.id)
        .not('borrower_id', 'in', `(${selectedIds.join(',')})`)
        .is('deleted_at', null)
        .limit(50);

      if (error) throw error;

      const suggestions: BorrowerWithUpdate[] = [];
      (data || []).forEach((item: any) => {
        const borrower = Array.isArray(item.borrowers) ? item.borrowers[0] : item.borrowers;
        if (!borrower) return;

        const addressLower = borrower.address.toLowerCase();
        let matchScore = 0;
        selectedAddresses.forEach(addr => {
          addr.toLowerCase().split(/\s+/).forEach(word => {
            if (word.length > 3 && addressLower.includes(word)) matchScore++;
          });
        });

        if (matchScore > 0) {
          suggestions.push({
            ...borrower,
            distance: matchScore,
            update_count: recentUpdates.get(borrower.id) || 0,
          });
        }
      });

      suggestions.sort((a, b) => {
        const scoreA = (a.distance || 0) * 2 + (a.update_count || 0);
        const scoreB = (b.distance || 0) * 2 + (b.update_count || 0);
        return scoreB - scoreA;
      });

      setSuggestedBorrowers(suggestions.slice(0, 5));
      if (suggestions.length > 0) setSuggestionsOpen(true);
    } catch (error: any) {
      console.error('Error loading nearby suggestions:', error);
    }
  };

  const searchBorrowers = async () => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from('borrowers')
        .select('id, name, mobile_no, address')
        .or(`name.ilike.%${searchTerm}%,address.ilike.%${searchTerm}%`)
        .is('deleted_at', null)
        .order('name')
        .limit(20);
      if (error) throw error;
      setSearchResults(data || []);
    } catch (error: any) {
      console.error('Error searching borrowers:', error);
      showError('Failed to search borrowers');
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    if (borrowerSearchOpen) searchBorrowers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, borrowerSearchOpen]);

  const addBorrower = (borrower: Borrower) => {
    if (selectedBorrowers.some(b => b.item_type === 'borrower' && b.borrower_id === borrower.id)) {
      showError('Borrower already in itinerary');
      return;
    }
    setSelectedBorrowers([
      ...selectedBorrowers,
      {
        item_type: 'borrower',
        borrower_id: borrower.id,
        borrower,
        visit_order: selectedBorrowers.length + 1,
        comments: '',
      },
    ]);
    setBorrowerSearchOpen(false);
    setSearchTerm('');
  };

  const addTask = () => {
    if (!taskTitle.trim()) {
      showError('Task title is required');
      return;
    }
    setSelectedBorrowers([
      ...selectedBorrowers,
      {
        item_type: 'task',
        task_title: taskTitle.trim(),
        task_description: taskDescription.trim(),
        visit_order: selectedBorrowers.length + 1,
        comments: '',
      },
    ]);
    setTaskDialogOpen(false);
    setTaskTitle('');
    setTaskDescription('');
  };

  const removeItem = (index: number) => {
    const reordered = selectedBorrowers
      .filter((_, i) => i !== index)
      .map((b, idx) => ({ ...b, visit_order: idx + 1 }));
    setSelectedBorrowers(reordered);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedBorrowers.length) return;
    const updated = [...selectedBorrowers];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSelectedBorrowers(updated.map((b, idx) => ({ ...b, visit_order: idx + 1 })));
  };

  const updateComment = (index: number, comment: string) => {
    setSelectedBorrowers(selectedBorrowers.map((item, idx) =>
      idx === index ? { ...item, comments: comment } : item
    ));
  };

  const updateTaskDetails = (index: number, title: string, description: string) => {
    setSelectedBorrowers(selectedBorrowers.map((item, idx) =>
      idx === index ? { ...item, task_title: title, task_description: description } : item
    ));
  };

  const submitItinerary = async () => {
    if (!userProfile || !plannedDate) return;
    if (selectedBorrowers.length === 0) {
      showError('Please add at least one item to the itinerary');
      return;
    }

    // When a regular agent edits their same-day approved itinerary, preserve the approved status
    const isSavingApprovedToday =
      currentItinerary?.status === 'approved' &&
      isToday(plannedDate) &&
      !canApproveAndEditApproved;

    setSaving(true);
    try {
      const dateStr = format(plannedDate, 'yyyy-MM-dd');
      const status = isSavingApprovedToday ? 'approved' : 'submitted';
      let itineraryId = currentItinerary?.id;

      if (itineraryId) {
        const { error: updateError } = await supabase
          .from('itineraries')
          .update({ status, submitted_at: new Date().toISOString() })
          .eq('id', itineraryId);
        if (updateError) throw updateError;

        const { error: deleteError } = await supabase
          .from('itinerary_borrowers')
          .delete()
          .eq('itinerary_id', itineraryId);
        if (deleteError) throw deleteError;
      } else {
        const { data: newItinerary, error: createError } = await supabase
          .from('itineraries')
          .insert({
            user_id: userProfile.id,
            planned_date: dateStr,
            status,
            submitted_at: new Date().toISOString(),
          })
          .select()
          .single();
        if (createError) throw createError;
        itineraryId = newItinerary.id;
      }

      const { error: insertError } = await supabase
        .from('itinerary_borrowers')
        .insert(
          selectedBorrowers.map(b => ({
            itinerary_id: itineraryId,
            item_type: b.item_type,
            borrower_id: b.item_type === 'borrower' ? b.borrower_id : null,
            task_title: b.item_type === 'task' ? b.task_title : null,
            task_description: b.item_type === 'task' ? b.task_description : null,
            visit_order: b.visit_order,
            comments: b.comments,
          }))
        );
      if (insertError) throw insertError;

      // Notify managers, admins, and superagents — but never the submitter themselves
      const { data: supervisors } = await supabase
        .from('profiles')
        .select('id')
        .in('role', ['manager', 'admin', 'superagent'])
        .neq('id', userProfile.id);

      if (supervisors && supervisors.length > 0) {
        const agentName = userProfile.username || 'An agent';
        const dateLabel = format(plannedDate, 'MMMM d, yyyy');
        const notifMessage = isSavingApprovedToday
          ? `${agentName} updated today's approved itinerary (${dateLabel}).`
          : `${agentName} submitted an itinerary for approval (${dateLabel}).`;
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 7);
        await supabase.from('notifications').insert(
          supervisors.map((s: any) => ({
            recipient_id: s.id,
            message: notifMessage,
            // Aligned with samofundmobile so manager/admin click-through works on the
            // shared notifications table (was 'collection'/'collection_case', which made
            // mobile's collection_case handler treat reference_id as a collection id and error).
            category: 'itinerary',
            notification_type: 'itinerary_submitted',
            is_read: false,
            is_active: true,
            reference_id: itineraryId,
            expiry_date: expiryDate.toISOString().split('T')[0],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }))
        );
      }

      showSuccess(isSavingApprovedToday ? 'Itinerary updated!' : 'Itinerary submitted for approval!');
      await loadItinerary(); // also resyncs savedBorrowers
    } catch (error: any) {
      console.error('Error saving itinerary:', error);
      showError('Failed to save itinerary');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'info' | 'warning' | 'default' => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'submitted': return 'info';
      case 'revision_requested': return 'warning';
      default: return 'default';
    }
  };

  const canEditItinerary = () => {
    if (!currentItinerary) return true;
    // Superagent/admin/manager can always edit any itinerary
    if (canApproveAndEditApproved) return true;
    // Agents can always edit their itinerary on the same day (things change in the field)
    if (plannedDate && isToday(plannedDate)) return true;
    // For future dates: only draft or revision_requested
    return currentItinerary.status === 'draft' || currentItinerary.status === 'revision_requested';
  };

  const isApproved = currentItinerary?.status === 'approved';
  const isCreator = currentItinerary?.user_id === userProfile?.id;

  const canSubmitForApproval = () => {
    if (!currentItinerary) return true;
    if (currentItinerary.status === 'approved' && canApproveAndEditApproved && !isCreator) return false;
    return true;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="md" sx={{ py: { xs: 1.5, sm: 4 }, px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 0, sm: 3 },
            border: { xs: 'none', sm: '1px solid' },
            borderColor: 'divider',
            borderRadius: { xs: 0, sm: 2 },
            bgcolor: { xs: 'transparent', sm: 'background.paper' },
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 2, sm: 3 } }}>
            <CalendarMonthIcon color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }} />
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold' }}>
              Itinerary Planner
            </Typography>
          </Box>

          {/* Date Picker & Status */}
          <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid item xs={12} sm={7}>
              <DatePicker
                label="Planned Date"
                value={plannedDate}
                onChange={(newValue) => setPlannedDate(newValue)}
                disabled={!canEdit}
                slotProps={{ textField: { fullWidth: true, size: isMobile ? 'small' : 'medium' } }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              {currentItinerary && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Status
                  </Typography>
                  <Chip
                    label={currentItinerary.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(currentItinerary.status)}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Approved alerts */}
          {isApproved && !canApproveAndEditApproved && plannedDate && isToday(plannedDate) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Approved — but today's plan can still be updated!
              </Typography>
              <Typography variant="body2">
                Add or remove stops, reorder visits, and save changes for today's itinerary.
                Use <strong>Approved Itinerary</strong> to post field activity updates.
              </Typography>
            </Alert>
          )}
          {isApproved && !canApproveAndEditApproved && plannedDate && !isToday(plannedDate) && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleOutlineIcon />}>
              <Typography variant="subtitle2" fontWeight="bold">This itinerary has been approved!</Typography>
              <Typography variant="body2">
                View and add field posts in the <strong>Approved Itinerary</strong> page. Past itineraries cannot be modified.
              </Typography>
            </Alert>
          )}
          {isApproved && canApproveAndEditApproved && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">Approved itinerary.</Typography>
              <Typography variant="body2">As a superagent/admin, you may still edit if unplanned things come up.</Typography>
            </Alert>
          )}

          {/* Manager Comment */}
          {currentItinerary?.manager_comment && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">Manager Comment:</Typography>
              <Typography variant="body2">{currentItinerary.manager_comment}</Typography>
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Priority Visit Reminders */}
              {canEditItinerary() && reminders.length > 0 && (
                <Card
                  sx={{
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'warning.main',
                    bgcolor: 'warning.50',
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Badge badgeContent={reminders.length} color="error">
                          <NotificationsIcon color="warning" />
                        </Badge>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">
                          Priority Visits
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => setRemindersOpen(!remindersOpen)}>
                        {remindersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={remindersOpen}>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {reminders.map((reminder) => (
                          <Card key={reminder.borrower.id} sx={{ bgcolor: 'background.paper', borderRadius: 1.5 }}>
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                    {reminder.borrower.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {reminder.borrower.address}
                                  </Typography>
                                  <Chip
                                    label={reminder.reason}
                                    size="small"
                                    color={reminder.priority === 'high' ? 'error' : reminder.priority === 'medium' ? 'warning' : 'default'}
                                    icon={<WarningIcon />}
                                    sx={{ mt: 0.5, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                                  />
                                </Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => addBorrower(reminder.borrower)}
                                  disabled={selectedBorrowers.some(
                                    b => b.item_type === 'borrower' && b.borrower_id === reminder.borrower.id
                                  )}
                                  sx={{ flexShrink: 0, minWidth: 64 }}
                                >
                                  {selectedBorrowers.some(b => b.item_type === 'borrower' && b.borrower_id === reminder.borrower.id)
                                    ? 'Added'
                                    : 'Add'}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </Collapse>
                  </CardContent>
                </Card>
              )}

              {/* Nearby Suggestions */}
              {canEditItinerary() && suggestedBorrowers.length > 0 && (
                <Card
                  sx={{
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'info.main',
                    bgcolor: 'info.50',
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon color="info" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Nearby Borrowers
                        </Typography>
                        <Chip label={`${suggestedBorrowers.length}`} size="small" color="info" />
                      </Box>
                      <IconButton size="small" onClick={() => setSuggestionsOpen(!suggestionsOpen)}>
                        {suggestionsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Borrowers near your current selections
                    </Typography>
                    <Collapse in={suggestionsOpen}>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {suggestedBorrowers.map((borrower) => (
                          <Card key={borrower.id} sx={{ bgcolor: 'background.paper', borderRadius: 1.5 }}>
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                    {borrower.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {borrower.address}
                                  </Typography>
                                  {borrower.update_count && borrower.update_count > 0 && (
                                    <Chip
                                      label={`${borrower.update_count} recent`}
                                      size="small"
                                      color="success"
                                      icon={<UpdateIcon />}
                                      sx={{ mt: 0.5 }}
                                    />
                                  )}
                                </Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  onClick={() => addBorrower(borrower)}
                                  disabled={selectedBorrowers.some(b => b.item_type === 'borrower' && b.borrower_id === borrower.id)}
                                  sx={{ flexShrink: 0 }}
                                >
                                  {selectedBorrowers.some(b => b.item_type === 'borrower' && b.borrower_id === borrower.id) ? 'Added' : 'Add'}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </Collapse>
                  </CardContent>
                </Card>
              )}

              {/* Itinerary Items */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">
                    Itinerary Items ({selectedBorrowers.length})
                  </Typography>
                  {canEditItinerary() && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setBorrowerSearchOpen(true)}
                        size={isMobile ? 'small' : 'medium'}
                      >
                        Add Borrower
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<TaskIcon />}
                        onClick={() => setTaskDialogOpen(true)}
                        size={isMobile ? 'small' : 'medium'}
                      >
                        Add Task
                      </Button>
                    </Box>
                  )}
                </Box>

                {selectedBorrowers.length === 0 ? (
                  <Alert severity="info">
                    No items yet. Tap <strong>Add Borrower</strong> to add stops or <strong>Add Task</strong> for other tasks.
                  </Alert>
                ) : (
                  <List disablePadding>
                    {selectedBorrowers.map((item, index) => (
                      <Card
                        key={index}
                        sx={{
                          mb: 1.5,
                          border: '1px solid',
                          borderColor: item.item_type === 'task' ? 'warning.light' : 'divider',
                          borderRadius: 2,
                        }}
                      >
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            {/* Order badge */}
                            <Box
                              sx={{
                                minWidth: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: item.item_type === 'task' ? 'warning.main' : 'primary.main',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.9rem',
                                flexShrink: 0,
                              }}
                            >
                              {item.visit_order}
                            </Box>

                            {/* Content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {item.item_type === 'borrower' ? (
                                <>
                                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {item.borrower?.name}
                                    </Typography>
                                    <Chip label="Borrower" size="small" color="primary" />
                                    {item.borrower_id &&
                                      recentUpdates.has(item.borrower_id) &&
                                      recentUpdates.get(item.borrower_id)! > 0 && (
                                        <Tooltip title={`${recentUpdates.get(item.borrower_id)} updates in last 7 days`}>
                                          <Chip
                                            label={`${recentUpdates.get(item.borrower_id)} updates`}
                                            size="small"
                                            color="success"
                                            icon={<UpdateIcon />}
                                          />
                                        </Tooltip>
                                      )}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    {item.borrower?.mobile_no} · {item.borrower?.address}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {item.task_title}
                                    </Typography>
                                    <Chip label="Task" size="small" color="warning" />
                                  </Box>
                                  {item.task_description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                      {item.task_description}
                                    </Typography>
                                  )}
                                  {canEditItinerary() && (
                                    <Box sx={{ mb: 1 }}>
                                      <TextField
                                        fullWidth
                                        label="Task Title"
                                        value={item.task_title}
                                        onChange={(e) => updateTaskDetails(index, e.target.value, item.task_description || '')}
                                        size="small"
                                        sx={{ mb: 1 }}
                                      />
                                      <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Description (optional)"
                                        value={item.task_description || ''}
                                        onChange={(e) => updateTaskDetails(index, item.task_title || '', e.target.value)}
                                        size="small"
                                      />
                                    </Box>
                                  )}
                                </>
                              )}

                              <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Notes (optional)"
                                value={item.comments}
                                onChange={(e) => updateComment(index, e.target.value)}
                                disabled={!canEditItinerary()}
                                placeholder="Add notes about this visit..."
                                size="small"
                              />
                            </Box>

                            {/* Move/Delete actions */}
                            {canEditItinerary() && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                                <Tooltip title="Move Up">
                                  <span>
                                    <IconButton size="small" onClick={() => moveItem(index, 'up')} disabled={index === 0}>
                                      <ArrowUpward fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Move Down">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveItem(index, 'down')}
                                      disabled={index === selectedBorrowers.length - 1}
                                    >
                                      <ArrowDownward fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Remove">
                                  <IconButton size="small" color="error" onClick={() => removeItem(index)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </List>
                )}
              </Box>

              {/* Submit / Save Button */}
              {canEditItinerary() && selectedBorrowers.length > 0 && canSubmitForApproval() && (
                <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                  {(() => {
                    const isSameDayApproved =
                      isApproved && plannedDate && isToday(plannedDate) && !canApproveAndEditApproved;
                    return (
                      <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={submitItinerary}
                        disabled={saving || currentItinerary?.status === 'submitted' || !hasChanges}
                        color={isSameDayApproved ? 'success' : 'primary'}
                        fullWidth={isMobile}
                        size={isMobile ? 'large' : 'medium'}
                        sx={{ borderRadius: 2 }}
                      >
                        {saving
                          ? (isSameDayApproved ? 'Saving…' : 'Submitting…')
                          : (isSameDayApproved ? 'Save Changes' : 'Submit for Approval')}
                      </Button>
                    );
                  })()}
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Add Task Dialog */}
        <Dialog
          open={taskDialogOpen}
          onClose={() => setTaskDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>Add Task</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              autoFocus
              label="Task Title"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              placeholder="e.g., Visit bank, Submit report…"
              sx={{ mt: 1, mb: 2 }}
              required
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description (optional)"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Details about this task…"
            />
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 1 } }}>
            <Button
              onClick={() => {
                setTaskDialogOpen(false);
                setTaskTitle('');
                setTaskDescription('');
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={addTask} disabled={!taskTitle.trim()}>
              Add Task
            </Button>
          </DialogActions>
        </Dialog>

        {/* Borrower Search Dialog */}
        <Dialog
          open={borrowerSearchOpen}
          onClose={() => setBorrowerSearchOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>Add Borrower</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              autoFocus
              label="Search by name or address"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
            />
            {searchLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : searchTerm.length < 2 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                Type at least 2 characters to search
              </Typography>
            ) : searchResults.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                No borrowers found
              </Typography>
            ) : (
              <List disablePadding>
                {searchResults.map((borrower) => (
                  <ListItem
                    key={borrower.id}
                    button
                    onClick={() => addBorrower(borrower)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      mb: 1,
                      py: 1.5,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {borrower.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {borrower.mobile_no} · {borrower.address}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 1 } }}>
            <Button onClick={() => setBorrowerSearchOpen(false)} fullWidth={isMobile}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
}
